import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, FileX } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback UI */
  fallback?: ReactNode;
  /** Error boundary name for logging */
  name?: string;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show detailed error info (dev mode) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Root-level error boundary component
 * Catches React rendering errors and displays user-friendly fallback UI
 *
 * Usage:
 * <ErrorBoundary name="App Root">
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { name, onError } = this.props;

    // Log error to console
    console.error(`[ErrorBoundary: ${name || 'Unknown'}]`, error, errorInfo);

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Optional: Send to error tracking service (Sentry, etc.)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     contexts: { react: { componentStack: errorInfo.componentStack } },
    //   });
    // }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    // Clear all state and return to landing
    localStorage.removeItem('zaplens_current_file');
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails = import.meta.env.DEV } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Default error UI
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Error Card */}
          <div className="bg-card border-2 border-destructive rounded-lg shadow-2xl p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-destructive" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-center mb-3 text-card-foreground">
              Something went wrong
            </h1>

            {/* Description */}
            <p className="text-center text-muted-foreground mb-8">
              We encountered an unexpected error. Don&apos;t worry, your data is safe and never leaves your browser.
            </p>

            {/* Error Message (if in dev mode) */}
            {showDetails && error && (
              <div className="mb-6 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                <p className="text-sm font-mono text-destructive mb-2 font-semibold">
                  {error.toString()}
                </p>
                {errorInfo && (
                  <details className="text-xs font-mono text-muted-foreground mt-2">
                    <summary className="cursor-pointer hover:text-foreground transition-colors">
                      Show stack trace
                    </summary>
                    <pre className="mt-2 overflow-auto max-h-48 p-2 bg-muted rounded">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors font-medium"
              >
                <Home className="w-4 h-4" />
                Go to Home
              </button>

              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
              >
                <FileX className="w-4 h-4" />
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-8 p-4 bg-muted/50 rounded-lg">
              <h3 className="text-sm font-semibold mb-2 text-foreground">What you can try:</h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Click &quot;Try Again&quot; to reset the application state</li>
                <li>Try uploading a different Zapier export file</li>
                <li>Reload the page to start fresh</li>
                <li>Clear your browser cache and try again</li>
              </ul>
            </div>

            {/* Privacy Note */}
            <p className="mt-6 text-xs text-center text-muted-foreground">
              🔒 Your workflow data never leaves your browser. This error occurred locally.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Specialized error boundary for file parsing operations
 * Provides more targeted error messages and recovery options
 */
export class FileParserErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[FileParserErrorBoundary]', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Clear the current file state
    if (typeof this.props.onError === 'function') {
      // Trigger a reset in the parent component
      window.dispatchEvent(new CustomEvent('clear-workflow-data'));
    }
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return children;
    }

    return (
      <div className="flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-card border border-destructive/50 rounded-lg shadow-lg p-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <FileX className="w-8 h-8 text-destructive" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-center mb-2 text-card-foreground">
            Failed to Parse Workflow
          </h2>

          <p className="text-sm text-center text-muted-foreground mb-4">
            We couldn&apos;t read your Zapier export file. This might happen if the file is corrupted or uses an unsupported format.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded text-xs font-mono text-destructive">
              {error.message}
            </div>
          )}

          <div className="space-y-2 mb-4 text-sm">
            <p className="font-semibold text-foreground">Try these solutions:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Re-export your workflow from Zapier</li>
              <li>Ensure the file is a valid JSON export</li>
              <li>Try a different workflow</li>
              <li>Check the file isn&apos;t corrupted</li>
            </ul>
          </div>

          <button
            onClick={this.handleReset}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Upload Another File
          </button>
        </div>
      </div>
    );
  }
}
